/**
 * Batch routine parameter introspection for MySQL schema warming.
 *
 * Builds the renderer-side `routinesByNameLower` map (keyed by lower-cased
 * routine name) consumed by `projectMysqlSnapshot` → schema bus. Two correctness
 * concerns motivate the query shape:
 *
 *   1. **Procedure vs. function kind** must reach the bus. Downstream the
 *      `CALL <ident>` slot (`mysqlResolver.ts`) filters routines by
 *      `kind === 'procedure'`, and `projectRoutines` (`projectionHelpers.ts`)
 *      maps the cache's `routineType` field to that kind via an uppercase
 *      `'PROCEDURE'` check. If `routineType` is undefined, EVERY routine
 *      defaults to `'function'` and `CALL <ident>` autocomplete surfaces
 *      nothing. The query therefore JOINs `information_schema.routines` to
 *      pick up `ROUTINE_TYPE`.
 *
 *   2. **Parameterless routines must still appear.** `information_schema.parameters`
 *      has zero rows for `CREATE PROCEDURE p() …`, so a parameters-only scan
 *      drops them entirely. The query drives off `information_schema.routines`
 *      with a LEFT JOIN so zero-parameter procedures/functions still produce
 *      one row (with NULL parameter columns), which we filter out at the
 *      grouping step.
 */

import type { RoutineMetadata, RoutineParameter } from '@/types/electron';
import type { IntrospectionRow } from './introspectionPaging';
import { ROUTINE_ROW_BUDGET } from './introspectionBudgets';
import { fetchPagedArtefact, type PagedFetchOptions } from './introspectionQuery';

function mapRow(row: Record<string, unknown>): RoutineParameter {
  const mode = row.mode != null ? String(row.mode).toUpperCase() : '';
  return {
    name: String(row.param_name ?? ''),
    dataType: String(row.formatted_type ?? row.data_type ?? ''),
    isOutput: mode === 'OUT' || mode === 'INOUT',
  };
}

/**
 * Pure aggregator — group raw rows from the
 * `routines LEFT JOIN parameters` query into the per-routine metadata map.
 * Exposed for unit tests so the grouping contract can be exercised without a
 * live MySQL.
 *
 * Contract:
 *   - One `RoutineMetadata` entry per distinct `routine_name`.
 *   - `routineType` is preserved verbatim from `r.ROUTINE_TYPE` so the bus
 *     projection can map `'PROCEDURE'` → `'procedure'` and `'FUNCTION'` →
 *     `'function'`.
 *   - Rows where `param_name IS NULL` (parameterless routines OR function
 *     RETURN rows) are kept for the routine but contribute no parameter.
 *   - Rows where `routine_name` is empty/missing are dropped (defensive).
 */
export function aggregateMysqlRoutineRows(
  rows: ReadonlyArray<Record<string, unknown>>,
  database: string,
): Record<string, RoutineMetadata[]> {
  type RoutineGroup = { routineType: string; returns?: string; parameters: RoutineParameter[] };
  const grouped = new Map<string, RoutineGroup>();
  for (const row of rows) {
    const routineName = String(row.routine_name ?? '');
    if (!routineName) continue;
    const routineType = String(row.routine_type ?? '');
    let group = grouped.get(routineName);
    if (!group) {
      group = {
        routineType,
        // `routines.DTD_IDENTIFIER` — the function's return type spelling
        // (`decimal(10,2)`); NULL for procedures (spec §A1).
        returns: row.return_type != null ? String(row.return_type) : undefined,
        parameters: [],
      };
      grouped.set(routineName, group);
    }
    // Only record a parameter when this row carries one. The LEFT JOIN places
    // a synthetic NULL-parameters row for parameterless routines — we keep the
    // routine itself but skip the parameter.
    if (row.param_name != null) {
      group.parameters.push(mapRow(row));
    }
  }

  const routinesByNameLower: Record<string, RoutineMetadata[]> = {};
  for (const [routineName, group] of grouped) {
    const key = routineName.toLowerCase();
    routinesByNameLower[key] = [
      {
        schema: database,
        name: routineName,
        routineType: group.routineType,
        ...(group.returns ? { returns: group.returns } : {}),
        parameters: group.parameters,
      },
    ];
  }
  return routinesByNameLower;
}

/**
 * LEFT JOIN information_schema.routines → information_schema.parameters keyed
 * by SPECIFIC_NAME + SPECIFIC_SCHEMA. The LEFT side guarantees one row per
 * routine even when the routine has zero parameters (in which case all
 * PARAMETER_* columns are NULL). PARAMETER_NAME IS NULL also marks the
 * function's RETURN row (we only want named parameters), so the join
 * predicate filters those out — keeping the routine but skipping the row.
 *
 * S7 item 8 — paged by `(ROUTINE_NAME, ORDINAL_POSITION)`. One routine is many
 * rows (one per parameter), so the paged loop carries a straddling routine to
 * the next page rather than aggregating it as two partial signatures.
 */
export function buildMysqlRoutineParamsPageSql(pageClause: string): string {
  return `SELECT
      r.ROUTINE_NAME AS routine_name,
      r.ROUTINE_TYPE AS routine_type,
      r.DTD_IDENTIFIER AS return_type,
      p.PARAMETER_NAME AS param_name,
      p.DATA_TYPE AS data_type,
      p.DTD_IDENTIFIER AS formatted_type,
      p.PARAMETER_MODE AS mode,
      p.ORDINAL_POSITION AS position
    FROM information_schema.routines r
    LEFT JOIN information_schema.parameters p
      ON p.SPECIFIC_SCHEMA = r.ROUTINE_SCHEMA
     AND p.SPECIFIC_NAME = r.SPECIFIC_NAME
     AND p.PARAMETER_NAME IS NOT NULL
    WHERE r.ROUTINE_SCHEMA = DATABASE()
    ORDER BY r.ROUTINE_NAME, p.ORDINAL_POSITION
    ${pageClause}`;
}

/** Page routine signatures into `sink`, one page's worth of routines at a time. */
export async function fetchMysqlRoutineParamPages(
  connectionId: string,
  database: string,
  sink: (routines: readonly RoutineMetadata[]) => void | Promise<void>,
  options?: PagedFetchOptions,
): Promise<void> {
  await fetchPagedArtefact<RoutineMetadata>({
    engine: 'mysql',
    dialect: 'limit-offset',
    connectionId,
    database,
    label: 'routine-params',
    artefact: 'routines',
    totalBudget: ROUTINE_ROW_BUDGET,
    sqlForPage: buildMysqlRoutineParamsPageSql,
    aggregate: (rows: readonly IntrospectionRow[]) =>
      Object.values(aggregateMysqlRoutineRows(rows, database)).flat(),
    sink,
    groupKeyOf: (row) => String(row.routine_name ?? ''),
    shouldAbort: options?.shouldAbort,
  });
}

/** Accumulating wrapper over {@link fetchMysqlRoutineParamPages}. */
export async function fetchMysqlRoutineParams(
  connectionId: string,
  database: string,
): Promise<Record<string, RoutineMetadata[]>> {
  const byName: Record<string, RoutineMetadata[]> = {};
  await fetchMysqlRoutineParamPages(connectionId, database, (page) => {
    for (const routine of page) (byName[routine.name.toLowerCase()] ??= []).push(routine);
  });
  return byName;
}
